// SelectorModal.tsx

import React, { useState, useEffect } from 'react';
import {
 Item,
 CartItemSelection,
 SelectionGroup,
 SelectionGroupItem,
 Modifier,
 getItemPrice,
 getSelectionItemPriceModifier,
 getModifierOptionPriceModifier,
} from '../../../../types';
import SubItems from './components/SubItems/SubItems';
import Modifiers from './components/Modifiers/Modifiers';
import Summary from './components/Summary/Summary';
import StockAlert from '../../../StockAlert/StockAlert';
import { uuidv4 } from '../../../../../../../../../utils/functions';
import { Modal } from 'react-bootstrap';
import { formatCurrency } from '../../../../../../../../../utils/functions';
import './SelectorModal.scss';
import { HiOutlineXMark } from 'react-icons/hi2';

interface SelectedSelectionGroupItems {
 [selectionGroupId: number]: {
   [itemId: number]: number;
 };
}

interface SelectionGroupItemComments {
 [selectionGroupId: number]: {
   [itemId: number]: string;
 };
}

interface SelectionGroupItemCourseTags {
 [selectionGroupId: number]: {
   [itemId: number]: 'ENTRADA' | 'FONDO' | undefined;
 };
}

interface SelectedModifiers {
 [modifierId: number]: number;
}

interface SelectorModalProps {
 show: boolean;
 onHide: () => void;
 cart: CartItemSelection[];
 setCart: React.Dispatch<React.SetStateAction<CartItemSelection[]>>;
 categoryItems: Item[];
 selectedItemId: number | null;
 setSelectedItemId: React.Dispatch<React.SetStateAction<number | null>>;
 editingIndex: number | null; // New prop to identify if editing
 setEditingIndex: React.Dispatch<React.SetStateAction<number | null>>;
 clientIndex: number; // New prop for clientIndex
 setClientIndex: React.Dispatch<React.SetStateAction<number>>;
 pedidoType: "Delivery" | "Caja" | "Restaurant" | "Autoservicio"; // Add pedidoType prop
 getItemStockInfo?: (itemId: number) => {
   hasShortage: boolean;
   hasWarning: boolean;
   shortageInfo?: any;
   warningInfo?: any;
 };
}

const SelectorModal: React.FC<SelectorModalProps> = ({
 show,
 onHide,
 cart,
 setCart,
 categoryItems,
 selectedItemId,
 setSelectedItemId,
 editingIndex,
 setEditingIndex,
 clientIndex,
 setClientIndex,
 pedidoType, // Add pedidoType to destructured props
 getItemStockInfo,
}) => {
 const [selectedItemQuantity, setSelectedItemQuantity] = useState<number>(1);
 const [selectedSelectionGroups, setSelectedSelectionGroups] =
   useState<SelectedSelectionGroupItems>({});
 const [selectionGroupItemComments, setSelectionGroupItemComments] =
   useState<SelectionGroupItemComments>({});
 const [selectionGroupItemCourseTags, setSelectionGroupItemCourseTags] =
   useState<SelectionGroupItemCourseTags>({});
 const [selectedModifiers, setSelectedModifiers] = useState<SelectedModifiers>(
   {}
 );
 const [comment, setComment] = useState<string>(''); // New state for comment
 const [selectedItem, setSelectedItem] = useState<Item | null>(null);
 const [isSelectionsValid, setIsSelectionsValid] = useState<boolean>(false); // New state for validation

 useEffect(() => {
   // Update selectedItem when selectedItemId changes
   const item = categoryItems.find((i) => i && i.item_id === selectedItemId) || null;
   
   // Validate item structure before setting
   if (item) {
     // Ensure required arrays exist
     if (!Array.isArray(item.selection_groups)) {
       console.warn('Item missing selection_groups, defaulting to empty array');
       item.selection_groups = [];
     }
     if (!Array.isArray(item.modifiers)) {
       console.warn('Item missing modifiers, defaulting to empty array');
       item.modifiers = [];
     }
   }
   
   setSelectedItem(item);
 }, [selectedItemId, categoryItems]);

 // Initialize selection groups, modifiers, comment, and clientIndex when a new item is selected or editing
 useEffect(() => {
   if (selectedItemId !== null ) {

     const item = categoryItems.find((i) => i.item_id === selectedItemId) || null;
     if (editingIndex !== null && cart[editingIndex]) {

       // Editing an existing cart item
       const cartItem = cart[editingIndex];
       setSelectedItemQuantity(cartItem.quantity);
       setComment(cartItem.comment || '');
       setClientIndex(cartItem.clientIndex);


       // Initialize selection groups
       const initSg: SelectedSelectionGroupItems = {};
       const initComments: SelectionGroupItemComments = {};
       const initCourseTags: SelectionGroupItemCourseTags = {};
       const item = categoryItems.find((i) => i.item_id === selectedItemId) || null;
       item?.selection_groups.forEach((sg) => {
         initSg[sg.selection_group_id] = {};
         initComments[sg.selection_group_id] = {};
         initCourseTags[sg.selection_group_id] = {};
         sg.items.forEach((i) => {
           const selectedItem = cartItem.selection_groups.find(
             (csg) => csg.selection_group_id === sg.selection_group_id
           )?.selected_items.find((si) => si.item_id === i.item_id);
           
           initSg[sg.selection_group_id][i.item_id] = selectedItem?.quantity || 0;
           initComments[sg.selection_group_id][i.item_id] = selectedItem?.comment || '';
           initCourseTags[sg.selection_group_id][i.item_id] = selectedItem?.course_tag;
         });
       });
        setSelectedSelectionGroups(initSg);
        setSelectionGroupItemComments(initComments);
        setSelectionGroupItemCourseTags(initCourseTags);


       // Initialize modifiers
       const initMods: SelectedModifiers = {};
       item?.modifiers.forEach((m) => {
         const chosenOptionId =
           cartItem.modifiers.find((cm) => cm.modifier_id === m.modifier_id)
             ?.option_id || (m.is_optional ? 0 : m.options[0]?.option_id || 0);
         initMods[m.modifier_id] = chosenOptionId;
       });
       setSelectedModifiers(initMods);
     } else {
        // Adding a new cart item
       setSelectedItemQuantity(1);
       setComment('');


       // Initialize selection groups
       const initSg: SelectedSelectionGroupItems = {};
       const initComments: SelectionGroupItemComments = {};
       const initCourseTags: SelectionGroupItemCourseTags = {};
       item?.selection_groups.forEach((sg) => {
         initSg[sg.selection_group_id] = {};
         initComments[sg.selection_group_id] = {};
         initCourseTags[sg.selection_group_id] = {};
         sg.items.forEach((i) => {
           initSg[sg.selection_group_id][i.item_id] = 0;
           initComments[sg.selection_group_id][i.item_id] = '';
           initCourseTags[sg.selection_group_id][i.item_id] = undefined;
         });
       });


       setSelectedSelectionGroups(initSg);
       setSelectionGroupItemComments(initComments);
       setSelectionGroupItemCourseTags(initCourseTags);


       // Initialize modifiers
       const initMods: SelectedModifiers = {};
       item?.modifiers.forEach((m) => {
         if (!m.is_optional && m.options.length > 0) {
           initMods[m.modifier_id] = m.options[0].option_id;
         } else {
           initMods[m.modifier_id] = 0;
         }
       });
       setSelectedModifiers(initMods);
     }
   } else {
     setSelectedItemQuantity(1);
     setSelectedSelectionGroups({});
     setSelectionGroupItemComments({});
     setSelectionGroupItemCourseTags({});
     setSelectedModifiers({});
     setComment('');
     setClientIndex(0);
   }
 }, [
   selectedItemId,
   categoryItems,
   editingIndex,
   cart,
   setClientIndex,
 ]);

 // Validate selections whenever selection groups change
 useEffect(() => {
   if (!selectedItemId) {
     setIsSelectionsValid(false);
     return;
   }

   let valid = true;
   const selectedItem = categoryItems.find((i) => i.item_id === selectedItemId);
   selectedItem?.selection_groups.forEach((sg) => {
     const selectedQty = Object.values(selectedSelectionGroups[sg.selection_group_id] || {}).reduce(
       (sum, val) => sum + val,
       0
     );
     if (selectedQty < sg.min_selections) {
       valid = false;
     }
     // Optionally, enforce max_selections here as well
   });

   setIsSelectionsValid(valid);
 }, [selectedSelectionGroups,selectedItemId,categoryItems]);

 // Calculate total price for the selected item configuration
 const calculateTotalPrice = (): number => {
   // If no item is selected, return a total of 0
   if (!selectedItem) return 0;

   let total = 0; // Initialize total price
   let hasPriceSetter = false; // Flag to indicate if any price setter modifier is selected
   let priceSetterTotal = 0; // Accumulator for price setter modifiers

   // Iterate over each modifier to check for price setters
   selectedItem.modifiers.forEach((m) => {
     const chosenOptionId = selectedModifiers[m.modifier_id];
     if (chosenOptionId) {
       const chosenOption = m.options.find((o) => o.option_id === chosenOptionId);
       if (chosenOption && m.is_price_setter) {
         hasPriceSetter = true;
         // Use pedidoType-specific price modifier
         priceSetterTotal += getModifierOptionPriceModifier(chosenOption, pedidoType);
       }
     }
   });

   if (hasPriceSetter) {
     // If any price setter modifiers are selected, set total to their sum
     total = priceSetterTotal;
   } else {
     // No price setter modifiers selected, proceed with standard calculation

     // Start with the item's base price (using pedidoType-specific price)
     total = getItemPrice(selectedItem, pedidoType);

     // Add prices from selected items within selection groups
     selectedItem.selection_groups.forEach((sg) => {
       const sgSelection = selectedSelectionGroups[sg.selection_group_id] || {};
       Object.keys(sgSelection).forEach((itemIdStr) => {
         const itemId = parseInt(itemIdStr, 10);
         const qty = sgSelection[itemId] || 0;
         const sgItem = sg.items.find((x) => x.item_id === itemId);
         if (sgItem && qty > 0) {
           // Use pedidoType-specific price modifier for selection group items
           total += getSelectionItemPriceModifier(sgItem, pedidoType) * qty;
         }
       });
     });

     // Add prices from selected modifier options
     selectedItem.modifiers.forEach((m) => {
       const chosenOptionId = selectedModifiers[m.modifier_id];
       if (chosenOptionId) {
         const chosenOption = m.options.find((o) => o.option_id === chosenOptionId);
         if (chosenOption) {
           // Use pedidoType-specific price modifier for modifier options
           total += getModifierOptionPriceModifier(chosenOption, pedidoType);
         }
       }
     });
   }

   // Multiply the total by the selected item's quantity
   total = total * selectedItemQuantity;

   return total; // Return the final calculated total
 };

 const totalPrice = calculateTotalPrice();

 // Helper function to get item name by item_id from selection_groups
 const getSelectionGroupItemName = (
   selection_group_id: number,
   item_id: number
 ): string => {
   const sg = selectedItem?.selection_groups.find(
     (sg) => sg.selection_group_id === selection_group_id
   );
   const item = sg?.items.find((i) => i.item_id === item_id);
   return item ? item.item_name : `Item ${item_id}`;
 };

 // Helper function to get option name by option_id from modifiers
 const getModifierOptionName = (modifier_id: number, option_id: number): string => {
   const modifier = selectedItem?.modifiers.find(
     (m) => m.modifier_id === modifier_id
   );
   const option = modifier?.options.find((o) => o.option_id === option_id);
   return option ? option.option_name : `Option ${option_id}`;
 };

 // Handle adding or editing cart items
 const handleAddOrEditToCart = () => {
   if (!selectedItemId) return;
   const selectedItem = categoryItems.find((i) => i.item_id === selectedItemId);
   if (!selectedItem) return;
   const cartItem: CartItemSelection = {
     selection_id:
       editingIndex !== null ? cart[editingIndex].selection_id : uuidv4(),
     item_id: selectedItem.item_id,
     item_name: selectedItem.item_name,
     quantity: selectedItemQuantity,
     base_price: getItemPrice(selectedItem, pedidoType), // Use pedidoType-specific price
     selection_groups: selectedItem.selection_groups.map((sg) => {
       const sgSelection = selectedSelectionGroups[sg.selection_group_id] || {};
       const sgComments = selectionGroupItemComments[sg.selection_group_id] || {};
       const sgCourseTags = selectionGroupItemCourseTags[sg.selection_group_id] || {};
       return {
         selection_group_id: sg.selection_group_id,
         selection_group_name: sg.selection_group_name,
         selected_items: Object.keys(sgSelection)
           .filter(
             (itemIdStr) => sgSelection[parseInt(itemIdStr, 10)] > 0
           )
           .map((itemIdStr) => {
             const itemId = parseInt(itemIdStr, 10);
             const sgItem = sg.items.find((x) => x.item_id === itemId);
             return {
               item_id: itemId,
               item_name: getSelectionGroupItemName(sg.selection_group_id, itemId),
               quantity: sgSelection[itemId],
               // Use pedidoType-specific price modifier
               price_modifier: sgItem ? getSelectionItemPriceModifier(sgItem, pedidoType) : 0,
               comment: sgComments[itemId] || undefined, // Include comment if it exists
               course_tag: sgCourseTags[itemId] || undefined, // Include course tag if it exists
             };
           }),
       };
     }),
     modifiers: selectedItem.modifiers
       .map((m) => {
         const chosenOptionId = selectedModifiers[m.modifier_id];
         return {
           modifier_id: m.modifier_id,
           option_id: chosenOptionId,
           option_name: getModifierOptionName(m.modifier_id, chosenOptionId), // Include option_name
         };
       })
       .filter((m) => m.option_id !== 0),
     total_price: totalPrice,
     comment: comment, // Include comment
     clientIndex: clientIndex, // Include clientIndex
   };

   if (editingIndex !== null) {
     // Update existing cart item
     setCart((prev) => {
       const newCart = [...prev];
       newCart[editingIndex] = cartItem;
       return newCart;
     });
     setEditingIndex(null);
   } else {
     // Add new item to cart
     setCart((prev) => [...prev, cartItem]);
   }

   // Reset selection after adding or editing
   setSelectedItemId(null);
   setSelectedItemQuantity(1);
   setSelectedSelectionGroups({});
   setSelectionGroupItemComments({});
   setSelectionGroupItemCourseTags({});
   setSelectedModifiers({});
   setComment('');
   onHide();
 };

 // Handle comment change for selection group items
 const handleSelectionGroupItemCommentChange = (
   selectionGroupId: number,
   itemId: number,
   comment: string
 ) => {
   setSelectionGroupItemComments((prev) => ({
     ...prev,
     [selectionGroupId]: {
       ...prev[selectionGroupId],
       [itemId]: comment,
     },
   }));
 };

 // Handle course tag toggle for selection group items
 const handleSelectionGroupItemCourseTagToggle = (
   selectionGroupId: number,
   itemId: number
 ) => {
   setSelectionGroupItemCourseTags((prev) => {
     const currentTag = prev[selectionGroupId]?.[itemId];
     const newTag = currentTag === 'ENTRADA' ? 'FONDO' : currentTag === 'FONDO' ? undefined : 'ENTRADA';
     
     return {
       ...prev,
       [selectionGroupId]: {
         ...prev[selectionGroupId],
         [itemId]: newTag,
       },
     };
   });
 };

 // Handle changes in selection group items
 const handleSelectionGroupItemChange = (
   sg: SelectionGroup,
   sgItem: SelectionGroupItem,
   increment: boolean
 ) => {

   setSelectedSelectionGroups((prev) => {
     const newSg = { ...prev };
     if (sg.min_selections === 1 && sg.max_selections === 1) {
       // Single Selection: Set only the selected item to 1, others to 0
       newSg[sg.selection_group_id] = {};
       newSg[sg.selection_group_id][sgItem.item_id] = increment ? 1 : 0;
     } else if (sg.min_selections === 0 && sg.max_selections === 1) {
       // Optional Single Selection: Toggle the selected item
       newSg[sg.selection_group_id][sgItem.item_id] = increment ? 1 : 0;
     } else {
       // Multiple Selections: Increment or decrement
       const currentQty = newSg[sg.selection_group_id][sgItem.item_id] || 0;


       if (typeof increment !== 'boolean') {
         // Handle unexpected types gracefully
         return newSg;
       }


       if (increment) {
         if (
           currentQty < sgItem.max_selections &&
           Object.values(newSg[sg.selection_group_id] || {}).reduce(
             (sum, val) => sum + val,
             0
           ) < sg.max_selections
         ) {
           newSg[sg.selection_group_id][sgItem.item_id] = currentQty + 1;
         }
       } else {
         if (currentQty > 0) {
           newSg[sg.selection_group_id][sgItem.item_id] = currentQty - 1;
         }
       }
     }

     return newSg;
   });
 };

 // Handle changes in modifier options
 const handleModifierOptionChange = (modifier: Modifier, optionId: number) => {
   setSelectedModifiers((prev) => ({
     ...prev,
     [modifier.modifier_id]: optionId,
   }));
 };
 return (
   <Modal
     show={show}
     onHide={() => {
       onHide();
       setEditingIndex(null);
       setSelectedItemId(null);
     }}
     aria-labelledby="selector-modal-title"
     centered
     scrollable
     className="selector-modal"
     size="lg"
     dialogClassName="selector-modal-dialog"
   >
     <Modal.Header>
         <Modal.Title id="selector-modal-title" className='titulo-modal-ver-datos-items'>
           {selectedItem?.item_name} <span className="price-badge">{formatCurrency(totalPrice)}</span>
         </Modal.Title>
        <button type="button" className="modal-close-button" onClick={onHide}>
          <HiOutlineXMark size={18} />
        </button>
     </Modal.Header>
     <Modal.Body className="selector-modal-body">
       {selectedItem && (
         <>
           {/* Stock Alert Banner */}
           {getItemStockInfo && (() => {
             const stockInfo = getItemStockInfo(selectedItem.item_id);
             return (
               <StockAlert
                 hasShortage={stockInfo.hasShortage}
                 hasWarning={stockInfo.hasWarning}
                 shortageInfo={stockInfo.shortageInfo}
                 warningInfo={stockInfo.warningInfo}
               />
             );
           })()}
           
           <p className="item-description">{selectedItem.description}</p>
           
           {/* Scrollable Area for Selection Groups and Modifiers */}
           <div className="selector-modal-scrollable-area">
             {/* Modifiers */}
             <Modifiers
               modifiers={selectedItem.modifiers}
               selectedModifiers={selectedModifiers}
               handleModifierOptionChange={handleModifierOptionChange}
             />

             {/* Selection Groups */}
             <SubItems
               selectionGroups={selectedItem.selection_groups}
               selectedSelectionGroups={selectedSelectionGroups}
               selectionGroupItemComments={selectionGroupItemComments}
               selectionGroupItemCourseTags={selectionGroupItemCourseTags}
               handleSelectionGroupItemChange={handleSelectionGroupItemChange}
               handleSelectionGroupItemCommentChange={handleSelectionGroupItemCommentChange}
               handleSelectionGroupItemCourseTagToggle={handleSelectionGroupItemCourseTagToggle}
               pedidoType={pedidoType}
               getItemStockInfo={getItemStockInfo}
             />
           </div>
         </>
       )}
     </Modal.Body>
     
     {/* Summary at the bottom */}
     {selectedItem && (
       <div className="selector-modal-footer">
         <Summary
           totalPrice={totalPrice}
           handleAddToCart={handleAddOrEditToCart}
           isEditing={editingIndex !== null}
           isDisabled={!isSelectionsValid}
         />
       </div>
     )}
   </Modal>
 );
};

export default SelectorModal;
